import { useState, useEffect } from "react";
import axios from "axios";
import styles from "../styles/page.module.css";
import ResponsiveAppBar from "./AppBar";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import RoleGuard from "./RoleGuard";
import DeleteWithPassword from "./deleteWithPassword";
import { API_BASE_URL } from "../api/config";

const BASE_URL = API_BASE_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ userId: "", password: "", name: "", role: "User" });
  const [editUser, setEditUser] = useState(null);
  const [alert, setAlert] = useState({ message: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const sessionData = JSON.parse(sessionStorage.getItem("user") || "null");
  const userRole = sessionData?.role;
  const token = sessionData?.token;

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/user/get_all_users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(response.data);
      } catch (error) {
        setAlert({
          message: error.response?.data?.message || "Error fetching users",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };
    if (token) fetchUsers();
    else navigate("/login");
  }, [token, navigate]);

  // Handle input changes for new user form
  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  // Handle input changes for edit user form
  const handleEditUserChange = (e) => {
    const { name, value } = e.target;
    setEditUser((prev) => ({ ...prev, [name]: value }));
  };

  // Add new user (Admin Manager only)
  const handleAddUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${BASE_URL}/user/add_user`, newUser, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlert({ message: "User added successfully!", type: "success" });
      setNewUser({ userId: "", password: "", name: "", role: "User" });
      // Refresh user list
      const response = await axios.get(`${BASE_URL}/user/get_all_users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      setAlert({
        message: error.response?.data?.message || "Error adding user",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Start editing a user
  const startEditUser = (user) => {
    setEditUser({ ...user, password: "" }); // Password is not fetched, require new input
  };

  // Update user (Admin Manager and Branch Admin)
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const updateData = {};
      if (editUser.name) updateData.name = editUser.name;
      if (editUser.role) updateData.role = editUser.role;
      if (editUser.password) updateData.password = editUser.password;

      await axios.put(
        `${BASE_URL}/user/get_users_by_id/updateUser/${editUser.userId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAlert({ message: "User updated successfully!", type: "success" });
      setEditUser(null);
      // Refresh user list
      const response = await axios.get(`${BASE_URL}/user/get_all_users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      setAlert({
        message: error.response?.data?.message || "Error updating user",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Delete user (Admin Manager only)
  const handleDeleteUser = async (userId) => {
    setIsLoading(true);
    try {
      await axios.delete(`${BASE_URL}/user/get_users_by_id/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlert({ message: "User deleted successfully!", type: "success" });
      // Refresh user list
      const response = await axios.get(`${BASE_URL}/user/get_all_users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      setAlert({
        message: error.response?.data?.message || "Error deleting user",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ResponsiveAppBar />
      <div className={`${styles.container} ${styles.mounted}`}>
        <div className={styles.card}>
          <div className={styles.cardContent}>
            <h1 className={styles.title}>User Management</h1>
            {alert.message && (
              <Alert severity={alert.type} sx={{ mb: 2 }}>
                {alert.message}
              </Alert>
            )}

            {/* Add User Form (Admin Manager Only) */}
            <RoleGuard allowedRoles={["Admin Manager"]}>
              <form onSubmit={handleAddUser} className={styles.form}>
                <h2 className={styles.subtitle}>Add New User</h2>
                <TextField
                  label="User ID"
                  name="userId"
                  value={newUser.userId}
                  onChange={handleNewUserChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <TextField
                  label="Name"
                  name="name"
                  value={newUser.name}
                  onChange={handleNewUserChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  value={newUser.password}
                  onChange={handleNewUserChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={newUser.role}
                    onChange={handleNewUserChange}
                    required
                  >
                    <MenuItem value="User">User</MenuItem>
                    <MenuItem value="Branch Admin">Branch Admin</MenuItem>
                    <MenuItem value="Admin Manager">Admin Manager</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                  sx={{ mt: 2 }}
                >
                  {isLoading ? <CircularProgress size={24} /> : "Add User"}
                </Button>
              </form>
            </RoleGuard>

            {/* User List */}
            <h2 className={styles.subtitle}>All Users</h2>
            {isLoading ? (
              <CircularProgress sx={{ display: "block", mx: "auto", my: 2 }} />
            ) : users.length === 0 ? (
              <p>No users found.</p>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Role</TableCell>
                      {(userRole === "Admin Manager" || userRole === "Branch Admin") && (
                        <TableCell>Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{user.userId}</TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.role}</TableCell>
                        {(userRole === "Admin Manager" || userRole === "Branch Admin") && (
                          <TableCell>
                            <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => startEditUser(user)}
                                disabled={isLoading}
                                sx={{ mr: 1 }}
                              >
                                Edit
                              </Button>
                            </RoleGuard>
                            <RoleGuard allowedRoles={["Admin Manager"]}>
                              <DeleteWithPassword
                                resourceId={user.userId}
                                resourceName="User"
                                onDelete={handleDeleteUser}
                                navigate={navigate}
                              />
                            </RoleGuard>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Edit User Dialog */}
            <Dialog open={!!editUser} onClose={() => setEditUser(null)}>
              <DialogTitle>Edit User</DialogTitle>
              <DialogContent>
                {editUser && (
                  <form>
                    <TextField
                      label="User ID"
                      value={editUser.userId}
                      fullWidth
                      margin="normal"
                      disabled
                    />
                    <TextField
                      label="Name"
                      name="name"
                      value={editUser.name}
                      onChange={handleEditUserChange}
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="Password (Leave blank to keep unchanged)"
                      name="password"
                      type="password"
                      value={editUser.password}
                      onChange={handleEditUserChange}
                      fullWidth
                      margin="normal"
                    />
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Role</InputLabel>
                      <Select
                        name="role"
                        value={editUser.role}
                        onChange={handleEditUserChange}
                      >
                        <MenuItem value="User">User</MenuItem>
                        <MenuItem value="Branch Admin">Branch Admin</MenuItem>
                        <MenuItem value="Admin Manager">Admin Manager</MenuItem>
                      </Select>
                    </FormControl>
                  </form>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEditUser(null)} disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateUser}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : "Update User"}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserManagement;